import { useEffect, useState, useCallback } from 'react'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

const money = (n, cur = 'CAD') => (n == null || n === '' ? '—' : `${cur} ${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
const th = { textAlign: 'left', padding: '8px 12px', fontSize: 11.5, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }
const td = { padding: '10px 12px', fontSize: 13, color: '#334155', verticalAlign: 'top' }
const inp = { padding: '7px 9px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box', marginTop: 3 }
const lbl = { fontSize: 12, color: '#475569', display: 'block', marginTop: 8 }
const card = { padding: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginTop: 12, maxWidth: 640 }
const h4 = { fontSize: 14, fontWeight: 700, color: '#0f172a' }
const btnDark = { padding: '7px 14px', fontSize: 12.5, fontWeight: 700, border: 'none', borderRadius: 6, background: '#0f172a', color: '#fff', cursor: 'pointer' }
const btnLight = { padding: '7px 12px', fontSize: 12.5, fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', color: '#334155', cursor: 'pointer' }
const linkBtn = { background: 'none', border: 'none', color: '#6d28d9', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }

// Shown sell / room / night: explicit sell wins, else net * (1 + markup/100).
function sellPerNight(net, sell, markup) {
  if (sell != null && sell !== '') { const v = Number(sell); return Number.isFinite(v) ? v : null }
  const n = Number(net), m = Number(markup)
  if (Number.isFinite(n) && markup != null && markup !== '' && Number.isFinite(m)) return n * (1 + m / 100)
  return null
}

export default function NetRateProperties() {
  const { t } = useLanguage()
  const [list, setList] = useState([])
  const [perm, setPerm] = useState({ canEditProperties: false, canEditPricing: false, canEditHouse: false })
  const [sel, setSel] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [adding, setAdding] = useState(false)

  const loadList = useCallback(() => {
    setLoading(true)
    tournamentApi.get('/portal/reseller/net-rate-properties')
      .then(r => { setList(r.data?.properties || []); setPerm({ canEditProperties: !!r.data?.canEditProperties, canEditPricing: !!r.data?.canEditPricing, canEditHouse: !!r.data?.canEditHouse }); setErr(null) })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { loadList() }, [loadList])

  const openProperty = useCallback((id) => {
    tournamentApi.get(`/portal/reseller/net-rate-properties/${id}`)
      .then(r => setSel(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }, [])

  return (
    <ResellerShell title={t('sp_nrc_title', 'Net-rate properties')}>
      <p style={{ color: '#64748b', fontSize: 14, marginTop: 0, marginBottom: 16, maxWidth: 760, lineHeight: 1.5 }}>
        {t('sp_nrc_subtitle', 'Hotels you book by hand (no supplier). Shared across resellers; you set your own net + price.')}
      </p>
      {err && <div style={{ marginBottom: 12, color: '#991b1b', fontSize: 13 }}>{err}</div>}
      {sel ? (
        <PropertyDetail sel={sel} perm={perm} allProps={list} t={t} onBack={() => { setSel(null); loadList() }} onReload={() => openProperty(sel.property.ID_NO)} />
      ) : loading ? (
        <div style={{ color: '#94a3b8', fontSize: 14, padding: '24px 0' }}>{t('sp_loading', 'Loading…')}</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
              <thead><tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={th}>{t('sp_nrc_name', 'Property name')}</th><th style={th}>{t('sp_nrc_address', 'Address')}</th>
                <th style={th}>{t('sp_nrc_my_rates', 'My rates')}</th><th style={th}>{t('sp_nrc_active', 'Active')}</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {list.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...td, color: '#94a3b8', textAlign: 'center', padding: 24 }}>{t('sp_nrc_empty', 'No net-rate properties yet.')}</td></tr>
                ) : list.map(p => (
                  <tr key={p.ID_NO} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ ...td, fontWeight: 600, color: '#0f172a' }}>{p.Name}</td>
                    <td style={{ ...td, color: '#64748b' }}>{p.AddressLine || '—'}</td>
                    <td style={td}>{p.myActiveRates || 0}</td>
                    <td style={td}>{p.IsActive ? t('sp_nrc_active', 'Active') : t('sp_nrc_inactive', 'Inactive')}</td>
                    <td style={{ ...td, textAlign: 'right' }}><button onClick={() => openProperty(p.ID_NO)} style={btnLight}>{t('sp_nrc_manage', 'Manage rates')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {perm.canEditPricing && (adding
            ? <AddProperty list={list} t={t} onCancel={() => setAdding(false)} onOpen={(id) => { setAdding(false); loadList(); openProperty(id) }} />
            : <button onClick={() => setAdding(true)} style={{ ...btnDark, marginTop: 12 }}>+ {t('sp_nrc_add_property', 'Add a property')}</button>)}
        </>
      )}
    </ResellerShell>
  )
}

function AddProperty({ list, t, onCancel, onOpen }) {
  const [f, setF] = useState({ name: '', addressLine: '', contactEmail: '', description: '' })
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null)
  const q = f.name.trim().toLowerCase()
  const matches = q ? list.filter(p => String(p.Name || '').toLowerCase().includes(q)).slice(0, 5) : []
  async function submit() {
    if (!f.name.trim()) { setErr(t('sp_nrc_need_name', 'Enter a property name.')); return }
    setBusy(true); setErr(null)
    try { const r = await tournamentApi.post('/portal/reseller/net-rate-properties', f); onOpen(r.data.property.ID_NO) }
    catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(false) }
  }
  return (
    <div style={card}>
      <div style={h4}>{t('sp_nrc_add_property', 'Add a property')}</div>
      <label style={lbl}>{t('sp_nrc_name', 'Property name')}<input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} style={inp} /></label>
      {matches.length > 0 && (
        <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: 8, marginTop: 6 }}>
          {t('sp_nrc_existing_hint', 'Already in the catalog? Pick it instead of adding a duplicate.')}
          {matches.map(m => <div key={m.ID_NO} style={{ marginTop: 4 }}><button onClick={() => onOpen(m.ID_NO)} style={linkBtn}>{m.Name}</button></div>)}
        </div>
      )}
      <label style={lbl}>{t('sp_nrc_address', 'Address')}<input value={f.addressLine} onChange={e => setF({ ...f, addressLine: e.target.value })} style={inp} /></label>
      <label style={lbl}>{t('sp_nrc_contact', 'Concierge contact email')}<input value={f.contactEmail} onChange={e => setF({ ...f, contactEmail: e.target.value })} style={inp} /></label>
      <label style={lbl}>{t('sp_nrc_desc', 'Description')}<textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} style={{ ...inp, minHeight: 60 }} /></label>
      {err && <div style={{ color: '#991b1b', fontSize: 12, marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={submit} disabled={busy} style={btnDark}>{busy ? t('sp_saving', 'Saving…') : t('sp_nrc_save', 'Save')}</button>
        <button onClick={onCancel} style={btnLight}>{t('sp_cancel', 'Cancel')}</button>
      </div>
    </div>
  )
}

function PropertyDetail({ sel, perm, allProps, t, onBack, onReload }) {
  const { property, rates, myAccountId } = sel
  const mine = (rates || []).filter(r => r.ResellerAccount_ID != null && Number(r.ResellerAccount_ID) === Number(myAccountId))
  const house = (rates || []).filter(r => r.ResellerAccount_ID == null)
  const canEditHouse = !!perm.canEditHouse
  // One editor at a time, across both tables. scope decides house vs own; rate
  // is the row being edited (null = new); prefillFrom seeds a NEW house rate
  // from an existing reseller rate (the "make this a house rate" promote).
  const [editing, setEditing] = useState(null) // null | { scope:'mine'|'house', rate, prefillFrom }
  return (
    <div>
      <button onClick={onBack} style={{ ...linkBtn, marginBottom: 12 }}>← {t('sp_nrc_back', 'All properties')}</button>
      {perm.canEditProperties
        ? <PropertyForm property={property} t={t} onSaved={onReload} />
        : <div style={card}>
            <div style={h4}>{property.Name}</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{property.AddressLine || '—'}</div>
            {property.ContactEmail && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{property.ContactEmail}</div>}
            <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>{t('sp_nrc_prop_edit_admin', 'Only InteGolf can edit this shared property. You manage your own rates below.')}</div>
          </div>}
      <div style={{ ...h4, marginTop: 20 }}>{t('sp_nrc_my_rates', 'My rates')}</div>
      <RateTable rows={mine} t={t} editable={perm.canEditPricing} onEdit={(r) => setEditing({ scope: 'mine', rate: r })}
        onPromote={canEditHouse ? (r) => setEditing({ scope: 'house', rate: null, prefillFrom: r }) : null} promoteLabel={t('sp_nrc_promote', 'Make house rate')} />
      {perm.canEditPricing && editing == null && <button onClick={() => setEditing({ scope: 'mine', rate: null })} style={{ ...btnDark, marginTop: 8 }}>+ {t('sp_nrc_add_rate', 'Add a rate')}</button>}
      {(house.length > 0 || canEditHouse) && (<>
        <div style={{ ...h4, marginTop: 20 }}>{canEditHouse ? t('sp_nrc_house_rates_edit', 'House rates (InteGolf)') : t('sp_nrc_house_rates', 'House rates (read-only)')}</div>
        {canEditHouse && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t('sp_nrc_house_hint', 'Shared with every reseller - pickable in the trip builder.')}</div>}
        <RateTable rows={house} t={t} editable={canEditHouse} onEdit={(r) => setEditing({ scope: 'house', rate: r })} />
        {canEditHouse && editing == null && <button onClick={() => setEditing({ scope: 'house', rate: null })} style={{ ...btnDark, marginTop: 8 }}>+ {t('sp_nrc_add_house_rate', 'Add a house rate')}</button>}
      </>)}
      {editing != null && <RateForm key={`${editing.scope}:${editing.rate?.ID_NO || 'new'}:${editing.prefillFrom?.ID_NO || ''}`}
        propertyId={property.ID_NO} scope={editing.scope} rate={editing.rate} prefillFrom={editing.prefillFrom} t={t}
        onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); onReload() }} />}
      {canEditHouse && <MergeProperty property={property} allProps={allProps} t={t} onMerged={onBack} />}
    </div>
  )
}

// Super-admin only: merge THIS (duplicate) property into a canonical one. Moves
// every rate (reseller-owned + house) onto the target, then deactivates this
// property. Two-step confirm; existing quotes/bookings are unaffected (they
// snapshot name + price at add-time and never reference the property row).
function MergeProperty({ property, allProps, t, onMerged }) {
  const targets = (allProps || []).filter(p => Number(p.ID_NO) !== Number(property.ID_NO) && p.IsActive)
  const [target, setTarget] = useState('')
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null)
  if (!targets.length) return null
  const canonName = targets.find(p => Number(p.ID_NO) === Number(target))?.Name || ''
  async function doMerge() {
    setBusy(true); setErr(null)
    try { const r = await tournamentApi.post(`/portal/reseller/net-rate-properties/${property.ID_NO}/merge`, { canonicalId: Number(target) }); onMerged(r.data) }
    catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(false) }
  }
  return (
    <div style={{ ...card, borderColor: '#fecaca', marginTop: 24 }}>
      <div style={h4}>{t('sp_nrc_merge_title', 'Merge this property')}</div>
      <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 4 }}>{t('sp_nrc_merge_hint', 'Moves every rate on this property onto the one you pick, then deactivates this property. Existing quotes and bookings are unaffected.')}</div>
      <label style={lbl}>{t('sp_nrc_merge_into', 'Merge into')}
        <select value={target} onChange={e => { setTarget(e.target.value); setConfirm(false) }} style={inp}>
          <option value="">{t('sp_nrc_merge_choose', 'Choose a property...')}</option>
          {targets.map(p => <option key={p.ID_NO} value={p.ID_NO}>{p.Name}</option>)}
        </select></label>
      {err && <div style={{ color: '#991b1b', fontSize: 12, marginTop: 8 }}>{err}</div>}
      {!confirm
        ? <button onClick={() => { if (!target) { setErr(t('sp_nrc_merge_pick', 'Pick a property to merge into.')); return } setErr(null); setConfirm(true) }} style={{ ...btnLight, marginTop: 10, borderColor: '#ef4444', color: '#b91c1c' }}>{t('sp_nrc_merge_btn', 'Merge...')}</button>
        : <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12.5, color: '#b91c1c', marginBottom: 6 }}>{t('sp_nrc_merge_confirm', 'Merge {dup} into {canon}? This deactivates {dup}.', { dup: property.Name, canon: canonName })}</div>
            <button onClick={doMerge} disabled={busy} style={{ ...btnDark, background: '#b91c1c', marginRight: 8 }}>{busy ? t('sp_saving', 'Saving...') : t('sp_nrc_merge_do', 'Yes, merge')}</button>
            <button onClick={() => setConfirm(false)} style={btnLight}>{t('sp_cancel', 'Cancel')}</button>
          </div>}
    </div>
  )
}

function PropertyForm({ property, t, onSaved }) {
  const [f, setF] = useState({ name: property.Name || '', addressLine: property.AddressLine || '', contactEmail: property.ContactEmail || '', description: property.Description || '', isActive: !!property.IsActive })
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null)
  async function save() {
    if (!f.name.trim()) { setErr(t('sp_nrc_need_name', 'Enter a property name.')); return }
    setBusy(true); setErr(null)
    try { await tournamentApi.patch(`/portal/reseller/net-rate-properties/${property.ID_NO}`, f); await onSaved() }
    catch (e) { setErr(e?.response?.data?.message || e.message) } finally { setBusy(false) }
  }
  return (
    <div style={card}>
      <div style={h4}>{property.Name}</div>
      <label style={lbl}>{t('sp_nrc_name', 'Property name')}<input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} style={inp} /></label>
      <label style={lbl}>{t('sp_nrc_address', 'Address')}<input value={f.addressLine} onChange={e => setF({ ...f, addressLine: e.target.value })} style={inp} /></label>
      <label style={lbl}>{t('sp_nrc_contact', 'Concierge contact email')}<input value={f.contactEmail} onChange={e => setF({ ...f, contactEmail: e.target.value })} style={inp} /></label>
      <label style={lbl}>{t('sp_nrc_desc', 'Description')}<textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} style={{ ...inp, minHeight: 60 }} /></label>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#475569', marginTop: 10 }}>
        <input type="checkbox" checked={f.isActive} onChange={e => setF({ ...f, isActive: e.target.checked })} /> {t('sp_nrc_active', 'Active')}
      </label>
      {err && <div style={{ color: '#991b1b', fontSize: 12, marginTop: 8 }}>{err}</div>}
      <button onClick={save} disabled={busy} style={{ ...btnDark, marginTop: 10 }}>{busy ? t('sp_saving', 'Saving…') : t('sp_nrc_save', 'Save')}</button>
    </div>
  )
}

function RateTable({ rows, t, editable, onEdit, onPromote, promoteLabel }) {
  const hasActions = editable || !!onPromote
  if (!rows.length) return <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>{t('sp_nrc_no_rates', 'No rates yet.')}</div>
  return (
    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, marginTop: 6 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead><tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <th style={th}>{t('sp_nrc_room_type', 'Room type')}</th><th style={th}>{t('sp_nrc_occupancy', 'Sleeps')}</th>
          <th style={th}>{t('sp_nrc_net', 'Net / room / night')}</th><th style={th}>{t('sp_nrc_sell_mode', 'Customer price')}</th>
          <th style={th}>{t('sp_nrc_refundable', 'Refundable')}</th><th style={th}>{t('sp_nrc_active', 'Active')}</th>{hasActions && <th style={th}></th>}
        </tr></thead>
        <tbody>
          {rows.map(r => {
            const sell = sellPerNight(r.NetPerRoomPerNight, r.SellPerRoomPerNight, r.MarkupPct)
            return (
              <tr key={r.ID_NO} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={td}>{r.RoomType || '—'}</td>
                <td style={td}>{r.Occupancy != null ? r.Occupancy : '—'}</td>
                <td style={td}>{money(r.NetPerRoomPerNight, r.Currency)}</td>
                <td style={td}>{sell != null ? money(sell, r.Currency) : '—'}{r.MarkupPct != null ? ` (${Number(r.MarkupPct)}%)` : ''}</td>
                <td style={td}>{r.Refundable ? t('sp_nrc_refundable', 'Refundable') : t('sp_nrc_nonref', 'Non-refundable')}</td>
                <td style={td}>{r.IsActive ? t('sp_nrc_active', 'Active') : t('sp_nrc_inactive', 'Inactive')}</td>
                {hasActions && <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {onPromote && <button onClick={() => onPromote(r)} style={{ ...btnLight, marginRight: 6 }}>{promoteLabel || t('sp_nrc_promote', 'Make house rate')}</button>}
                  {editable && <button onClick={() => onEdit(r)} style={btnLight}>{t('sp_nrc_edit', 'Edit')}</button>}
                </td>}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function RateForm({ propertyId, scope = 'mine', rate, prefillFrom, t, onCancel, onSaved }) {
  // Seed from the row being edited, else from a rate being promoted to house
  // (a NEW house rate copied from a reseller rate). `rate` set = PATCH; else POST.
  const src = rate || prefillFrom || null
  const isHouse = scope === 'house'
  const [f, setF] = useState(() => ({
    roomType: src?.RoomType || '', occupancy: src?.Occupancy != null ? String(src.Occupancy) : '', currency: src?.Currency || 'CAD',
    netPerRoomPerNight: src?.NetPerRoomPerNight != null ? String(src.NetPerRoomPerNight) : '',
    sellMode: src?.MarkupPct != null ? 'markup' : 'sell',
    sellPerRoomPerNight: src?.SellPerRoomPerNight != null ? String(src.SellPerRoomPerNight) : '',
    markupPct: src?.MarkupPct != null ? String(src.MarkupPct) : '',
    validFrom: src?.ValidFrom ? String(src.ValidFrom).slice(0, 10) : '', validTo: src?.ValidTo ? String(src.ValidTo).slice(0, 10) : '',
    refundable: src ? !!src.Refundable : false, cancellationText: src?.CancellationText || '', isActive: src ? !!src.IsActive : true,
  }))
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null)
  const preview = sellPerNight(f.netPerRoomPerNight, f.sellMode === 'sell' ? f.sellPerRoomPerNight : null, f.sellMode === 'markup' ? f.markupPct : null)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })
  async function save() {
    setBusy(true); setErr(null)
    const body = {
      roomType: f.roomType, occupancy: f.occupancy, currency: f.currency, netPerRoomPerNight: f.netPerRoomPerNight,
      sellPerRoomPerNight: f.sellMode === 'sell' ? f.sellPerRoomPerNight : null, markupPct: f.sellMode === 'markup' ? f.markupPct : null,
      validFrom: f.validFrom || null, validTo: f.validTo || null, refundable: f.refundable, cancellationText: f.cancellationText, isActive: f.isActive,
      // Only consequential on create: writes ResellerAccount_ID = NULL. The
      // backend refuses it for non-InteGolf-admins; update reads house-ness from
      // the stored row, so this is ignored on PATCH.
      houseRate: isHouse,
    }
    try {
      if (rate) await tournamentApi.patch(`/portal/reseller/net-rate-properties/rates/${rate.ID_NO}`, body)
      else await tournamentApi.post(`/portal/reseller/net-rate-properties/${propertyId}/rates`, body)
      await onSaved()
    } catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(false) }
  }
  return (
    <div style={card}>
      <div style={h4}>{isHouse
        ? (rate ? t('sp_nrc_edit_house_rate', 'Edit house rate') : t('sp_nrc_add_house_rate', 'Add a house rate'))
        : (rate ? t('sp_nrc_edit_rate', 'Edit rate') : t('sp_nrc_add_rate', 'Add a rate'))}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ ...lbl, flex: 2 }}>{t('sp_nrc_room_type', 'Room type')}<input value={f.roomType} onChange={set('roomType')} style={inp} /></label>
        <label style={{ ...lbl, flex: 1 }}>{t('sp_nrc_occupancy', 'Sleeps')}<input type="number" min="1" value={f.occupancy} onChange={set('occupancy')} style={inp} /></label>
        <label style={{ ...lbl, flex: 1 }}>{t('sp_nrc_currency', 'Currency')}<input value={f.currency} onChange={set('currency')} style={inp} /></label>
      </div>
      <label style={lbl}>{t('sp_nrc_net', 'Net / room / night')}<input type="number" min="0" step="0.01" value={f.netPerRoomPerNight} onChange={set('netPerRoomPerNight')} style={inp} /></label>
      <div style={lbl}>{t('sp_nrc_sell_mode', 'Customer price')}</div>
      <div style={{ display: 'flex', gap: 12, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: '#475569', display: 'flex', gap: 5, alignItems: 'center' }}><input type="radio" checked={f.sellMode === 'sell'} onChange={() => setF({ ...f, sellMode: 'sell' })} /> {t('sp_nrc_sell_explicit', 'Set a sell / room / night')}</label>
        <label style={{ fontSize: 13, color: '#475569', display: 'flex', gap: 5, alignItems: 'center' }}><input type="radio" checked={f.sellMode === 'markup'} onChange={() => setF({ ...f, sellMode: 'markup' })} /> {t('sp_nrc_sell_markup', 'Add a markup %')}</label>
      </div>
      {f.sellMode === 'sell'
        ? <input type="number" min="0" step="0.01" value={f.sellPerRoomPerNight} onChange={set('sellPerRoomPerNight')} style={inp} />
        : <input type="number" step="0.01" value={f.markupPct} onChange={set('markupPct')} style={inp} />}
      {preview != null && <div style={{ fontSize: 12, color: '#0f766e', marginTop: 4 }}>{t('sp_nrc_sell_preview', 'Sell / room / night: {amount}', { amount: money(preview, f.currency) })}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ ...lbl, flex: 1 }}>{t('sp_nrc_valid_from', 'Valid from')}<input type="date" value={f.validFrom} onChange={set('validFrom')} style={inp} /></label>
        <label style={{ ...lbl, flex: 1 }}>{t('sp_nrc_valid_to', 'Valid to')}<input type="date" value={f.validTo} onChange={set('validTo')} style={inp} /></label>
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: '#475569', display: 'flex', gap: 5, alignItems: 'center' }}><input type="radio" checked={f.refundable === true} onChange={() => setF({ ...f, refundable: true })} /> {t('sp_nrc_refundable', 'Refundable')}</label>
        <label style={{ fontSize: 13, color: '#475569', display: 'flex', gap: 5, alignItems: 'center' }}><input type="radio" checked={f.refundable === false} onChange={() => setF({ ...f, refundable: false })} /> {t('sp_nrc_nonref', 'Non-refundable')}</label>
        <label style={{ fontSize: 13, color: '#475569', display: 'flex', gap: 5, alignItems: 'center' }}><input type="checkbox" checked={f.isActive} onChange={e => setF({ ...f, isActive: e.target.checked })} /> {t('sp_nrc_active', 'Active')}</label>
      </div>
      <label style={lbl}>{t('sp_nrc_cancellation', 'Cancellation terms')}<textarea value={f.cancellationText} onChange={set('cancellationText')} style={{ ...inp, minHeight: 48 }} /></label>
      {err && <div style={{ color: '#991b1b', fontSize: 12, marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={save} disabled={busy} style={btnDark}>{busy ? t('sp_saving', 'Saving…') : t('sp_nrc_save', 'Save')}</button>
        <button onClick={onCancel} style={btnLight}>{t('sp_cancel', 'Cancel')}</button>
      </div>
    </div>
  )
}
